import { AsyncPipe } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { AddSpeedDialDialogComponent } from '@app/call-history/components/add-speed-dial-dialog/add-speed-dial-dialog.component';
import { ManageSpeedDialsDialogComponent } from '@app/call-history/components/manage-speed-dials-dialog/manage-speed-dials-dialog.component';
import { SpeedDialService } from '@app/call-history/services/speed-dial.service';
import { ContactService } from '@app/contacts/services/contact.service';
import { AppFeature } from '@app/core/models/config.models';
import { AppConfigService } from '@app/core/services/app-config.service';
import { AppTranslateService } from '@app/core/services/app-translate.service';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import { ButtonType } from '@app/shared/models/dialog-data';
import { TranslateModule } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { CopySpeedDialsDialogComponent } from './copy-speed-dials-dialog/copy-speed-dials-dialog.component';

interface SpeedDialGroup {
  key: string;
  site: string | null;
  department: string | null;
  count: number;
}

@Component({
  selector: 'app-speed-dial-preferences',
  templateUrl: './speed-dial.component.html',
  styleUrls: ['./speed-dial.component.scss'],
  standalone: true,
  imports: [
    AsyncPipe,
    FormsModule,
    MatButtonModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatSelectModule,
    TranslateModule,
  ],
})
export class SpeedDialPreferencesComponent {
  protected readonly speedDialGroups$: Observable<SpeedDialGroup[]>;
  protected readonly sites$: Observable<string[]>;
  protected readonly departments$: Observable<string[]>;
  protected selectedSite: string | null = null;
  protected selectedDepartment: string | null = null;
  protected readonly AppFeature = AppFeature;

  constructor(
    private speedDialService: SpeedDialService,
    private contactService: ContactService,
    private dialog: MatDialog,
    protected appConfigService: AppConfigService,
    private translateService: AppTranslateService
  ) {
    this.sites$ = this.contactService.data$.pipe(map(() => this.contactService.allPBXSites));
    this.departments$ = this.contactService.data$.pipe(map(() => this.contactService.allPBXDepartments));

    this.speedDialGroups$ = this.speedDialService.speedDials$.pipe(
      map((dials) => {
        const systemDials = dials.filter((d) => d.system);

        // Collect unique (site, department) group keys from actual dials
        const groupMap = new Map<string, { site: string | null; department: string | null }>();
        for (const dial of systemDials) {
          const key = `${dial.site ?? ''}|${dial.department ?? ''}`;
          if (!groupMap.has(key)) {
            groupMap.set(key, { site: dial.site, department: dial.department });
          }
        }

        // Count uses the same wildcard rule as the manage dialog filter:
        // a null on the *dial's* axis is a wildcard and counts under every
        // more-specific row, while a null on the *group's* axis is literal —
        // so (All, All) counts only truly-global dials and (Site, Dept)
        // counts (Site, Dept) + (Site, null) + (null, Dept) + (null, null).
        return [...groupMap.entries()]
          .map(([key, { site, department }]) => ({
            key,
            site,
            department,
            count: systemDials.filter(
              (d) => (d.site === null || d.site === site) && (d.department === null || d.department === department)
            ).length,
          }))
          .sort((a, b) => {
            if ((a.site ?? '') !== (b.site ?? '')) return (a.site ?? '').localeCompare(b.site ?? '');
            return (a.department ?? '').localeCompare(b.department ?? '');
          });
      })
    );
  }

  protected openManageDialog(group: SpeedDialGroup): void {
    this.dialog.open(ManageSpeedDialsDialogComponent, {
      data: { site: group.site, department: group.department, systemOnly: true },
      disableClose: false,
    });
  }

  protected openAddDialog(): void {
    this.dialog.open(AddSpeedDialDialogComponent, {
      data: {
        system: true,
        site: this.selectedSite,
        department: this.selectedDepartment,
      },
      disableClose: false,
    });
  }

  protected openCopyFromDialog(): void {
    this.dialog.open(CopySpeedDialsDialogComponent);
  }

  protected confirmDeleteGroup(group: SpeedDialGroup): void {
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        description: this.translateService.instant('SYSTEM_SPEED_DIALS.DELETE_CONFIRM', {
          site: group.site ?? this.translateService.instant('SYSTEM_SPEED_DIALS.ALL'),
          department: group.department ?? this.translateService.instant('SYSTEM_SPEED_DIALS.ALL'),
        }),
        buttons: [
          {
            label: this.translateService.instant('SYSTEM_SPEED_DIALS.CANCEL'),
            action: () => null,
            type: ButtonType.matFlatButton,
          },
          {
            label: this.translateService.instant('SYSTEM_SPEED_DIALS.DELETE'),
            action: async () => await this.speedDialService.deleteSystemSpeedDialsByGroup(group.site, group.department),
            type: ButtonType.matRaisedButton,
            default: true,
            color: 'warn',
          },
        ],
      },
    });
  }
}
